import { useMemo, useState } from 'react';
import { Funnel } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Label } from '@/components/ui-kit/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { cn } from '@/lib/utils';
import type { FilterFieldConfig, FilterValue } from '../../config/filterable-fields';
import { useGetLists } from '@/modules/lists/hooks/use-lists';
import {
  ContactsCustomFieldFilterSection,
  countActiveCustomFieldFilters,
} from './contacts-custom-field-filter-section';

const EMAIL_STATUS_OPTIONS = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'pending', label: 'Pending' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'suppressed', label: 'Suppressed (any status)' },
] as const;
/** SMS has no `?smsSuppressed=` query — Suppressed is email-channel only via `?suppressed=`. */
const SMS_STATUS_OPTIONS = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'pending', label: 'Pending' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
] as const;

export type ContactsFiltersDraft = {
  emailStatuses: string[];
  smsStatuses: string[];
  listName: string;
  fieldFilters: Record<string, FilterValue>;
};

type ContactsFiltersDropdownProps = {
  emailStatuses: string[];
  smsStatuses: string[];
  listName: string;
  customFields: FilterFieldConfig[];
  fieldFilters: Record<string, FilterValue>;
  onApply: (draft: ContactsFiltersDraft) => void;
  className?: string;
};

const toggleValue = (values: string[], value: string, checked: boolean): string[] => {
  if (checked) return values.includes(value) ? values : [...values, value];
  return values.filter((entry) => entry !== value);
};

const buildDraftFromApplied = (
  emailStatuses: string[],
  smsStatuses: string[],
  listName: string,
  fieldFilters: Record<string, FilterValue>
): ContactsFiltersDraft => ({
  emailStatuses: [...emailStatuses],
  smsStatuses: [...smsStatuses],
  listName,
  fieldFilters: { ...fieldFilters },
});

const buildEmptyDraft = (customFields: FilterFieldConfig[]): ContactsFiltersDraft => ({
  emailStatuses: [],
  smsStatuses: [],
  listName: '',
  fieldFilters: Object.fromEntries(
    customFields.map((field) => [
      field.key,
      field.operators.includes('numberRange') || field.operators.includes('dateRange')
        ? { from: '', to: '' }
        : (field.defaultValue ?? ''),
    ])
  ),
});

export const ContactsFiltersDropdown = ({
  emailStatuses,
  smsStatuses,
  listName,
  customFields,
  fieldFilters,
  onApply,
  className,
}: ContactsFiltersDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ContactsFiltersDraft>(() =>
    buildDraftFromApplied(emailStatuses, smsStatuses, listName, fieldFilters)
  );

  const { data: listsData } = useGetLists({
    pageNo: 1,
    pageSize: 50,
  });

  const lists = useMemo(
    () =>
      (listsData?.items ?? [])
        .map((list) => list.name.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [listsData?.items]
  );

  const activeCount =
    emailStatuses.length +
    smsStatuses.length +
    (listName ? 1 : 0) +
    countActiveCustomFieldFilters(fieldFilters, customFields);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft(buildDraftFromApplied(emailStatuses, smsStatuses, listName, fieldFilters));
    }
    setOpen(nextOpen);
  };

  const handleDone = () => {
    onApply(draft);
    setOpen(false);
  };

  const handleClearDraft = () => {
    setDraft(buildEmptyDraft(customFields));
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn('h-10 shrink-0 gap-2 px-4 text-sm font-medium', className)}
        >
          <Funnel className="size-4 shrink-0" aria-hidden />
          Filters
          {activeCount > 0 ? (
            <span className="ml-0.5 rounded-full bg-primary-50 px-1.5 text-xs font-medium text-primary-600">
              {activeCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex w-[26rem] max-w-[calc(100vw-2rem)] max-h-[min(32rem,calc(100vh-8rem))] flex-col overflow-hidden p-0"
      >
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Email status
            </p>
            <div className="flex flex-col gap-2">
              {EMAIL_STATUS_OPTIONS.map((status) => {
                const id = `contacts-filter-email-${status.value}`;
                return (
                  <div key={status.value} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={draft.emailStatuses.includes(status.value)}
                      onCheckedChange={(checked) =>
                        setDraft((prev) => ({
                          ...prev,
                          emailStatuses: toggleValue(
                            prev.emailStatuses,
                            status.value,
                            checked === true
                          ),
                        }))
                      }
                    />
                    <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                      {status.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              SMS status
            </p>
            <div className="flex flex-col gap-2">
              {SMS_STATUS_OPTIONS.map((status) => {
                const id = `contacts-filter-sms-${status.value}`;
                return (
                  <div key={status.value} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={draft.smsStatuses.includes(status.value)}
                      onCheckedChange={(checked) =>
                        setDraft((prev) => ({
                          ...prev,
                          smsStatuses: toggleValue(
                            prev.smsStatuses,
                            status.value,
                            checked === true
                          ),
                        }))
                      }
                    />
                    <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                      {status.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>

          {lists.length > 0 ? (
            <div className="mb-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                List
              </p>
              <div className="flex flex-col gap-2">
                {lists.map((name) => {
                  const id = `contacts-filter-list-${name}`;
                  return (
                    <div key={name} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={draft.listName === name}
                        onCheckedChange={(checked) =>
                          setDraft((prev) => ({
                            ...prev,
                            listName: checked === true ? name : '',
                          }))
                        }
                      />
                      <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                        {name}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {customFields.map((field) => (
            <ContactsCustomFieldFilterSection
              key={field.key}
              field={field}
              value={draft.fieldFilters[field.key]}
              onChange={(value) =>
                setDraft((prev) => ({
                  ...prev,
                  fieldFilters: { ...prev.fieldFilters, [field.key]: value },
                }))
              }
            />
          ))}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t bg-popover px-4 py-3">
          <button
            type="button"
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={handleClearDraft}
          >
            Clear all
          </button>
          <button
            type="button"
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
            onClick={handleDone}
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const getChannelFilterChips = (emailStatuses: string[], smsStatuses: string[]) => {
  const chips: { id: string; label: string; type: 'email' | 'sms'; value: string }[] = [];
  emailStatuses.forEach((value) => {
    const optionLabel =
      EMAIL_STATUS_OPTIONS.find((option) => option.value === value)?.label ??
      `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    chips.push({
      id: `email-${value}`,
      type: 'email',
      value,
      label: `Email: ${optionLabel}`,
    });
  });
  smsStatuses.forEach((value) => {
    const optionLabel =
      SMS_STATUS_OPTIONS.find((option) => option.value === value)?.label ??
      `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    chips.push({
      id: `sms-${value}`,
      type: 'sms',
      value,
      label: `SMS: ${optionLabel}`,
    });
  });
  return chips;
};
